import {BaseController, handleMultilingual} from '@wix/wixstores-client-storefront-sdk';
import {ControllerParams} from '@wix/yoshi-flow-editor';
import {IWidgetController} from '@wix/native-components-infra/dist/src/types/types';
import {ThankYouPageController} from './ThankYouPageController';

type AbstractConstructorHelper<T> = (new (...args: any) => {[x: string]: any}) & T;
type AbstractConstructorParameters<T> = ConstructorParameters<AbstractConstructorHelper<T>>;
export type ControllerConstructorType<T> = new (...args: AbstractConstructorParameters<BaseController>) => T;

type AppDataWithReportError = {
  reportError?: (error: Error) => void;
};

export const controllerFactory =
  <T extends ThankYouPageController>(ControllerConstructor: ControllerConstructorType<T>) =>
  (controllerParams: ControllerParams): IWidgetController => {
    const siteStore = controllerParams?.appData?.context.siteStore;
    let controller: T | undefined;

    const getController = async () => {
      if (controller) {
        return controller;
      }
      await siteStore.loadClientConfig();
      controller = new ControllerConstructor(controllerParams);
      return controller;
    };

    return {
      async pageReady() {
        try {
          const controllerInstance = await getController();

          const isMultilingualNeeded = () => {
            const multilingualFields = siteStore.getMultiLangFields();
            return (
              controllerParams.controllerConfig.config.externalId &&
              multilingualFields &&
              !multilingualFields?.isPrimaryLanguage
            );
          };

          const shouldCallAppSettings = isMultilingualNeeded();

          await Promise.all([
            ...(shouldCallAppSettings ? [handleMultilingual(controllerParams, controllerInstance.getFreeTexts())] : []),
            controllerInstance.init(),
          ]);
          controllerParams.controllerConfig.wixCodeApi.location.onChange(() => void controllerInstance?.load());
        } catch (error) {
          /* istanbul ignore next */
          console.error(error);
          /* istanbul ignore next */
          (controllerParams.appData as AppDataWithReportError)?.reportError?.(error as Error);
        }
      },
      exports() {
        return getExportsAfterControllerLoaded(getController, ['getOrder']);
      },
      updateConfig: (_$w, updatedConfig: {[key: string]: any}): void => {
        return void controller?.onStyleUpdate?.(updatedConfig.style.styleParams);
      },
    };
  };

function getExportsAfterControllerLoaded(getController: () => Promise<ThankYouPageController>, keys: string[]) {
  return keys.reduce<Record<string, (...args: any[]) => any>>((acc, key) => {
    acc[key] = async (...args: any[]) => {
      const resolvedController = await getController();
      return resolvedController.exports()[key](...args);
    };
    return acc;
  }, {});
}
